import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { toast } from 'sonner';
import {
  addToQueue,
  getQueueCount,
  getQueueItems,
  markQueueItemSynced,
  removeQueueItem,
  clearSyncedItems,
  cacheWorkOrders,
  getCachedWorkOrders,
  getUnsyncedPhotos,
  markPhotoSynced
} from '../lib/offlineDB';
import { API_URL } from '../lib/config';
// v1.3.16 — Manual Airplane Mode integration. `effectiveOnline`
// combines `navigator.onLine` with the user's manual toggle so
// draining waits for BOTH the network AND the user to be ready.
// When the feature flag is off, `subscribeConnectivity` reduces
// to the same online/offline events this file already listened
// for and `effectiveOnline === navigator.onLine` — behavior is
// byte-identical to pre-v1.3.16.
import { subscribeConnectivity, isEffectivelyOnline, getManualAirplaneMode } from '../lib/connectivity';

const OfflineContext = createContext(null);

export const useOffline = () => {
  const context = useContext(OfflineContext);
  if (!context) {
    throw new Error('useOffline must be used within an OfflineProvider');
  }
  return context;
};

export const OfflineProvider = ({ children }) => {
  // v1.3.16 — `isOnline` on this context now means "effective online"
  // (network AND user hasn't manually toggled airplane mode). This
  // is what every existing consumer already wanted: "should I
  // treat the app as online right now?". Keeping the field name
  // preserves the existing consumer contract.
  const [isOnline, setIsOnline] = useState(isEffectivelyOnline());
  const [manualAirplane, setManualAirplane] = useState(getManualAirplaneMode());
  const [queueCount, setQueueCount] = useState(0);
  const [isSyncing, setIsSyncing] = useState(false);
  const [lastSyncTime, setLastSyncTime] = useState(null);

  // Monitor effective online/offline status.
  //
  // v1.3.16 — Subscribes to a single stream of connectivity events
  // (network up/down + manual airplane toggle) via
  // `subscribeConnectivity`. Behavior in the two pre-existing modes
  // is UNCHANGED:
  //   * Fully online (net up, manual off): `isOnline` stays true.
  //   * True device offline (net down): `isOnline` flips to false.
  // The NEW state (net up, manual on) also flips `isOnline` to
  // false and holds it there until the user turns manual mode off.
  // Because `syncQueue` already checks `isOnline` before running,
  // this automatically prevents drain during manual airplane mode
  // without any extra guard.
  useEffect(() => {
    const unsub = subscribeConnectivity(({ effectiveOnline, manualAirplane: nextManual, networkOnline }) => {
      setManualAirplane(nextManual);
      setIsOnline(prev => {
        if (prev === effectiveOnline) return prev;
        // Toast on the transition, but only for the "back online"
        // path we already toasted for. The manual toggle emits its
        // own toast from the AirplaneModeToggle component, so we
        // don't double-toast that transition.
        if (effectiveOnline && !prev && networkOnline && !nextManual) {
          toast.success('Back online! Syncing changes...', { id: 'back-online', duration: 3000 });
        } else if (!effectiveOnline && prev && !networkOnline) {
          toast.warning('You are offline. Changes will sync when connected.', { duration: 5000 });
        }
        return effectiveOnline;
      });
    });
    return unsub;
  }, []);

  // Update queue count.
  //
  // v1.2.106 — Aggregate the pending count across ALL four offline
  // stores so the header pill / orange indicator reflects arch
  // captures + observations + existing-asset edits, not just the
  // legacy asset queue.
  //   1. Legacy asset queue     (lib/offlineDB.getQueueCount)
  //   2. Arch capture queue     (services/offlineArchitecturalQueue)
  //   3. Observation queue      (services/offlineObservationQueue)
  //   4. Existing-asset queue   (services/offlineExistingAssetQueue)
  //
  // All four are READ-ONLY here. We never touch any queue's write or
  // drain path — this only changes what number is displayed. The
  // three extra stores are pulled via dynamic import + try/catch so
  // this can't take out the app if one of the modules changes shape.
  const updateQueueCount = useCallback(async () => {
    const [legacyCount, archCount, obsCount, existingAssetCount] = await Promise.all([
      getQueueCount().catch(() => 0),
      import('../services/offlineArchitecturalQueue')
        .then(m => (m.getArchitecturalQueueStats
          ? m.getArchitecturalQueueStats().then(s => s?.totalCaptures || 0)
          : 0))
        .catch(() => 0),
      import('../services/offlineObservationQueue')
        .then(m => (m.getPendingObservations
          ? m.getPendingObservations().then(rows => (Array.isArray(rows) ? rows.length : 0))
          : 0))
        .catch(() => 0),
      import('../services/offlineExistingAssetQueue')
        .then(m => (m.getPendingExistingAssetCount
          ? m.getPendingExistingAssetCount()
          : 0))
        .catch(() => 0),
    ]);
    setQueueCount((legacyCount || 0) + archCount + obsCount + existingAssetCount);
  }, []);

  // v1.2.106 — Light polling so the count refreshes while the user
  // is capturing offline (the arch / observation / existing-asset
  // queues do their own writes without going through this context,
  // so a mount-only read would miss them). 4s cadence matches the
  // in-app OfflineStatusBanner's polling and stays well under any
  // IndexedDB read budget.
  useEffect(() => {
    const id = setInterval(() => { updateQueueCount(); }, 4000);
    const onFocus = () => updateQueueCount();
    const onNetChange = () => updateQueueCount();
    window.addEventListener('focus', onFocus);
    window.addEventListener('online', onNetChange);
    window.addEventListener('offline', onNetChange);
    return () => {
      clearInterval(id);
      window.removeEventListener('focus', onFocus);
      window.removeEventListener('online', onNetChange);
      window.removeEventListener('offline', onNetChange);
    };
  }, [updateQueueCount]);

  useEffect(() => {
    updateQueueCount();
  }, [updateQueueCount]);

  // Queue an API request for later sync
  const queueRequest = useCallback(async (request) => {
    const { method, url, data, description } = request;
    
    await addToQueue({
      type: 'api',
      method,
      url,
      data,
      description: description || `${method} ${url}`
    });
    
    await updateQueueCount();
    
    if (!isOnline) {
      toast.info(`Saved offline: ${description || 'Request queued'}`, { duration: 2000 });
    }
    
    return { queued: true };
  }, [isOnline, updateQueueCount]);

  // Sync queued requests
  const syncQueue = useCallback(async () => {
    if (!isOnline || isSyncing) return;

    setIsSyncing(true);
    const token = localStorage.getItem('token');
    
    if (!token) {
      setIsSyncing(false);
      return;
    }

    try {
      const items = await getQueueItems();
      const unsyncedItems = items.filter(i => !i.synced);
      
      let successCount = 0;
      let failCount = 0;

      for (const item of unsyncedItems) {
        try {
          const response = await fetch(`${API_URL}${item.url}`, {
            method: item.method,
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${token}`
            },
            body: item.data ? JSON.stringify(item.data) : undefined
          });

          if (response.ok) {
            await markQueueItemSynced(item.id);
            successCount++;
          } else {
            failCount++;
            // Don't remove failed items - they'll retry next sync
            console.error(`Sync failed for ${item.description}:`, await response.text());
          }
        } catch (err) {
          failCount++;
          console.error(`Sync error for ${item.description}:`, err);
        }
      }

      // Clean up synced items
      await clearSyncedItems();
      await updateQueueCount();

      if (successCount > 0) {
        toast.success(`Synced ${successCount} offline change${successCount > 1 ? 's' : ''}`, { duration: 3000 });
      }
      if (failCount > 0) {
        toast.error(`${failCount} change${failCount > 1 ? 's' : ''} failed to sync`, { duration: 3000 });
      }

      // Sync photos
      await syncPhotos(token);

      setLastSyncTime(new Date());
    } catch (err) {
      console.error('Queue sync error:', err);
    } finally {
      setIsSyncing(false);
    }
  }, [isOnline, isSyncing, updateQueueCount]);

  // Sync offline photos
  const syncPhotos = async (token) => {
    const photos = await getUnsyncedPhotos();
    
    for (const photo of photos) {
      try {
        const formData = new FormData();
        
        // Convert base64 to blob if needed
        let blob;
        if (typeof photo.data === 'string' && photo.data.startsWith('data:')) {
          const response = await fetch(photo.data);
          blob = await response.blob();
        } else {
          blob = photo.data;
        }
        
        formData.append('file', blob, `offline_photo_${photo.id}.jpg`);
        formData.append('work_order_id', photo.work_order_id);
        if (photo.metadata?.description) {
          formData.append('description', photo.metadata.description);
        }

        const response = await fetch(`${API_URL}/api/work-orders/${photo.work_order_id}/images`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`
          },
          body: formData
        });

        if (response.ok) {
          const result = await response.json();
          await markPhotoSynced(photo.id, result.url || result.firebase_url);
        }
      } catch (err) {
        console.error('Photo sync error:', err);
      }
    }
  };

  // Pre-cache work orders for offline access
  const cacheWorkOrdersForOffline = useCallback(async (workOrders) => {
    await cacheWorkOrders(workOrders);
  }, []);

  // Get cached work orders when offline
  const getOfflineWorkOrders = useCallback(async () => {
    return getCachedWorkOrders();
  }, []);

  // Manual sync trigger
  const triggerSync = useCallback(async () => {
    if (!isOnline) {
      toast.error('Cannot sync while offline');
      return;
    }
    await syncQueue();
  }, [isOnline, syncQueue]);

  // Auto-sync when coming online
  useEffect(() => {
    if (isOnline && queueCount > 0) {
      syncQueue();
    }
  }, [isOnline, queueCount, syncQueue]);

  const value = {
    isOnline,           // effective online (network AND !manualAirplane)
    // v1.3.16 — new fields for consumers that need to distinguish
    // the two reasons the app might be offline. All existing
    // consumers keep using `isOnline` and see identical behavior.
    manualAirplaneMode: manualAirplane,
    networkOnline: (typeof navigator !== 'undefined') ? navigator.onLine !== false : true,
    queueCount,
    isSyncing,
    lastSyncTime,
    queueRequest,
    syncQueue,
    triggerSync,
    cacheWorkOrdersForOffline,
    getOfflineWorkOrders
  };

  return (
    <OfflineContext.Provider value={value}>
      {children}
    </OfflineContext.Provider>
  );
};

export default OfflineContext;
